import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KompisHomeDelivery, KompisZipIn } from '@inter-ikea-kompis/react-components';
import { IZipAvailability } from '@inter-ikea-kompis/types/lib';
import { reportSetPostcode } from 'analytics/reporters';
import { SpaceProps } from 'styled-system';
import Box from 'ui/Box';
import { useAvailability } from 'contexts/availability';
import { focusRestoreToLocalStorage, setLastFocusedElementId } from 'utils/focusRestore';
import { ZipCodeCookieInformationService } from '@inter-ikea-kompis/services';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ZipInCardStateEnum, ZipInModalEnum } from '@inter-ikea-kompis/component-zip-in';
import { useServiceSettings } from 'contexts/serviceSettings';

export enum ReportCurrentPageEnum {
  productInfo = 'product_info',
  hamburgerMenu = 'hamburger_menu',
  summaryPage = 'summary_page',
  availabiltySheet = 'availability_sheet',
  bookAppointmentSheet = 'book_appointment_sheet',
  paxGuideRecommendations = 'pax_guide_recommendations',
  landingPage = 'landing_page',
  accountName = 'account_name'
}

type Props = SpaceProps & {
  zipAvailability?: IZipAvailability[];
  currentPage: ReportCurrentPageEnum;
  buttonId?: string;
};

function HomeDelivery({ zipAvailability, currentPage, buttonId, ...props }: Props) {
  const [visibleModal, setVisibleModal] = useState<any>(null);
  const [state, setState] = useState(ZipInCardStateEnum.default);

  const { dexfSettings, translations } = useServiceSettings();
  const { zipCode, validateZipCode } = useAvailability();
  const zipService = useMemo(
    () => new ZipCodeCookieInformationService({ settings: dexfSettings }),
    [dexfSettings]
  );

  const getAvailability = useCallback(
    async (code: string) => {
      setState(ZipInCardStateEnum.loading);
      const status = await validateZipCode(code);

      if (!status) {
        setState(ZipInCardStateEnum.invalidInput);
        return;
      }

      zipService.setZipCode(code);
      setState(ZipInCardStateEnum.submitted);
      setVisibleModal(undefined);
    },
    [validateZipCode, zipService]
  );

  useEffect(() => {
    if (!zipCode) {
      setState(ZipInCardStateEnum.default);
    } else {
      setState(ZipInCardStateEnum.submitted);
    }
  }, [zipCode]);

  const onSave = useCallback(
    async (event: CustomEvent) => {
      await getAvailability(event.detail.zipCode);
      reportSetPostcode({ page: currentPage });
    },
    [currentPage, getAvailability]
  );

  if (dexfSettings.kompis.serviceSettings.zipAvailabilityDataSource === 'disabled') {
    return null;
  }

  const onClose = () => {
    focusRestoreToLocalStorage();
  };

  if (!translations || !dexfSettings) {
    return null;
  }

  return (
    <Box {...props}>
      <KompisZipIn
        theme={SkapaTheme}
        translations={translations.kompis}
        settings={dexfSettings}
        visibleModal={visibleModal}
        zipCode={zipCode}
        state={state}
        onModalOpen={(event: CustomEvent) => {
          if (buttonId !== undefined) {
            setLastFocusedElementId(buttonId);
          }
          setVisibleModal(event.detail.visibleModal);
        }}
        onModalClose={() => {
          setVisibleModal(undefined);
          onClose();
        }}
        onForget={event => {
          setState(event.detail.state);
          setVisibleModal(event.detail.visibleModal);
          zipService.clearZipCode();
          onClose();
        }}
        onSave={onSave}
      />
      <KompisHomeDelivery
        theme={SkapaTheme}
        zipAvailabilities={zipCode ? zipAvailability : undefined}
        zipCode={zipCode}
        translations={translations.kompis}
        settings={dexfSettings}
        onHomeDeliveryClick={() => {
          if (buttonId !== undefined) {
            setLastFocusedElementId(buttonId);
          }
          setVisibleModal(ZipInModalEnum.zipIn);
        }}
      />
    </Box>
  );
}

export default HomeDelivery;
